import type { MorphSource } from "@/components/ui/Cteamsgallery";

/**
 * The ONE team modal's canonical state. Three modes on an existing team —
 * regular (present), edit, deploy — with deploy reachable ONLY on an existing
 * subject (a team-less deploy state does not exist; the open input requires a
 * team id). Every product entry (landing details, hero deploy, session chip,
 * settings banner, netflix tiles, URL restores, replicate) routes through this
 * state instead of owning a local overlay copy.
 */

export enum TeamModalStatus {
  Closed = "closed",
  Open = "open",
}

export enum TeamModalMode {
  /** The regular read state (the ratified docs' "View"). */
  Present = "present",
  Edit = "edit",
  Deploy = "deploy",
}

export enum TeamModalEditIntent {
  /** Edit the team record in place (owned/writable teams). */
  Update = "update",
  /** Edit into a new saved copy (public/read-only teams). */
  Copy = "copy",
}

export enum TeamModalSubjectKind {
  Existing = "existing",
  /** A team that does not exist yet — the build-your-own creation draft. */
  New = "new",
}

export interface TeamModalExistingSubject {
  kind: TeamModalSubjectKind.Existing;
  teamId: string;
}

/** The record-less creation subject: no id exists until Save creates one. */
export interface TeamModalNewSubject {
  kind: TeamModalSubjectKind.New;
}

export type TeamModalSubject =
  | TeamModalExistingSubject
  | TeamModalNewSubject;

/**
 * Morph/focus/layer metadata of the entry that opened the modal — geometry
 * only, never behavior: no open path may branch on where the modal came from.
 */
export interface TeamModalOrigin {
  /** The clicked source card's rect + face (null = centre grow). */
  morphSource: MorphSource | null;
  /** Opened from inside a rising modal — the overlay must clear its portal. */
  aboveModals: boolean;
  /** Element to restore focus to once the modal fully closes. */
  returnFocus?: HTMLElement | null;
}

/**
 * Mode + its intent as one closed shape: an edit intent can only exist while
 * the mode is Edit.
 */
export type TeamModalModeState =
  | { mode: TeamModalMode.Present }
  | { mode: TeamModalMode.Deploy }
  | { mode: TeamModalMode.Edit; editIntent: TeamModalEditIntent };

export interface TeamModalClosedState {
  status: TeamModalStatus.Closed;
}

export interface TeamModalOpenExistingState {
  status: TeamModalStatus.Open;
  subject: TeamModalExistingSubject;
  modeState: TeamModalModeState;
  origin: TeamModalOrigin;
}

/**
 * Creation: the New subject exists ONLY in edit. Its mode state carries no
 * intent field — creating IS the intent — and it names no other mode, so a
 * present or deploy reading on a record-less subject is unrepresentable
 * rather than merely guarded. On create success the subject swaps to
 * `{kind: Existing, teamId}` in place (mode Present, same open generation).
 */
export interface TeamModalOpenNewState {
  status: TeamModalStatus.Open;
  subject: TeamModalNewSubject;
  modeState: { mode: TeamModalMode.Edit };
  origin: TeamModalOrigin;
}

export type TeamModalOpenState =
  | TeamModalOpenExistingState
  | TeamModalOpenNewState;

export type TeamModalState = TeamModalClosedState | TeamModalOpenState;

/** Narrow an open state to the creation variant (the discriminant is nested
 *  on the subject, which plain control flow cannot narrow the parent by). */
export function isNewSubjectOpenState(
  openState: TeamModalOpenState,
): openState is TeamModalOpenNewState {
  return openState.subject.kind === TeamModalSubjectKind.New;
}

/**
 * Open input: deploy and present carry no intent; edit requires one.
 */
export type OpenExistingTeamModalInput = {
  teamId: string;
  origin: TeamModalOrigin;
} & (
  | { mode: TeamModalMode.Present | TeamModalMode.Deploy; editIntent?: never }
  | { mode: TeamModalMode.Edit; editIntent: TeamModalEditIntent }
);

/** Build-your-own: an empty subject with edit invoked. No mode/intent input —
 *  a New subject admits nothing else. */
export interface OpenNewTeamModalInput {
  origin: TeamModalOrigin;
}

export interface TeamModalContextValue {
  state: TeamModalState;
  openExisting: (input: OpenExistingTeamModalInput) => void;
  /**
   * Open the one modal on an EMPTY team with edit invoked (all build CTAs).
   * Back/cancel closes the modal (nothing presentable behind a New subject);
   * save creates the team and lands its regular state in place.
   */
  openNew: (input: OpenNewTeamModalInput) => void;
  /**
   * Innermost back: deploy returns to that team's regular state in place;
   * regular closes. An active edit routes through the overlay's guarded close
   * (the discard confirm intercepts a dirty draft) rather than a silent
   * mode drop.
   */
  back: () => void;
  /**
   * Close the whole modal through its dirty guards + collapse flight.
   * `afterClose` runs ONLY once the guards actually let the close through, so
   * a caller that opens something in the modal's place (the landing's All
   * Teams) cannot open it behind a discard confirm the user then cancels.
   */
  close: (afterClose?: () => void) => void;
}

export const TEAM_MODAL_CLOSED_STATE: TeamModalClosedState = {
  status: TeamModalStatus.Closed,
};

/** Origin for entries with no source geometry: centre grow, normal layering. */
export const TEAM_MODAL_CENTER_ORIGIN: TeamModalOrigin = {
  morphSource: null,
  aboveModals: false,
};
